import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Modal, 
  Input, 
  List, 
  Typography, 
  Empty, 
  Spin, 
  Tag, 
  Space,
  Divider,
  Avatar,
  Badge,
  Button,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  MessageOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BASE_URL } from "../../config";
import './SearchMessages.css';

const { Text, Paragraph } = Typography;
const { Search } = Input;

interface SearchResult {
  message: {
    event_id: string;
    room_id: string;
    sender: string;
    created: number;
    type: string;
    content: {
      body: string;
      formatted_body?: string;
      msgtype: string;
      reply_to?: any;
    };
    me: boolean;
    edited?: boolean;
    fileInfo?: any;
  };
  snippet: string;
  matchPosition: number;
  context: {
    before: any[];
    after: any[];
    target?: any; // Само найденное сообщение (полный объект)
  };
  roomId: string;
}

interface SearchMessagesProps {
  visible: boolean;
  onClose: () => void;
  roomId: string;
  roomName?: string;
  onJumpToMessage?: (eventId: string, contextMessages?: { before: any[], after: any[] }) => void;
}

const SearchMessages: React.FC<SearchMessagesProps> = ({
  visible,
  onClose,
  roomId,
  roomName = 'Room',
  onJumpToMessage
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc'); // 'desc' = новые первые, 'asc' = старые первые
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Ref для input чтобы установить фокус
  const searchInputRef = useRef<any>(null);

  // Очищаем результаты при закрытии модала и устанавливаем фокус при открытии
  useEffect(() => {
    if (!visible) {
      setSearchQuery('');
      setResults([]);
      setSearchPerformed(false);
      setTotalResults(0);
      setSortOrder('desc'); // Сбрасываем на сортировку по умолчанию
      setCurrentPage(1);
      setPageSize(20);
    } else {
      // Устанавливаем фокус на input при открытии модала
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [visible]);

  // Функция поиска
  const handleSearch = useCallback(async (query: string) => {
    if (!query || query.trim() === '') {
      setResults([]);
      setSearchPerformed(false);
      return;
    }

    setLoading(true);
    setSearchPerformed(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/search`, {
        params: {
          query: query.trim(),
          roomId: roomId, // Поиск только в текущей комнате
          limit: 10000, // Большой лимит для получения всех результатов
          contextSize: 0
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setResults(response.data.results);
        setTotalResults(response.data.totalResults);
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  // Автоматический поиск с debounce (задержка после окончания печатания)
  useEffect(() => {
    // Не запускаем автоматический поиск для пустого запроса
    if (!searchQuery || searchQuery.trim() === '') {
      return;
    }

    // Устанавливаем таймер на 800ms после окончания печатания
    const debounceTimer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 800);

    // Очищаем предыдущий таймер при новом вводе
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchQuery, handleSearch]);

  // Форматирование даты
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  // Экранирование специальных символов регулярных выражений
  const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Подсветка совпадений в тексте
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query) return text;
    
    // Экранируем специальные символы в поисковом запросе
    const escapedQuery = escapeRegExp(query);
    
    try {
      const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
      return (
        <span>
          {parts.map((part, index) => 
            part.toLowerCase() === query.toLowerCase() ? (
              <mark key={index} className="search-highlight">{part}</mark>
            ) : (
              <span key={index}>{part}</span>
            )
          )}
        </span>
      );
    } catch (error) {
      // Если всё равно возникла ошибка с регулярным выражением, просто возвращаем текст
      console.error('Error in highlightMatch:', error);
      return text;
    }
  };

  // Обработка клика на результат
  const handleResultClick = (result: SearchResult) => {
    if (onJumpToMessage) {
      // Передаем eventId и контекстные сообщения
      onJumpToMessage(result.message.event_id, result.context);
      onClose();
    }
  };

  // Переключение порядка сортировки
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении сортировки
  };

  // Отсортированные результаты
  const sortedResults = useMemo(() => {
    const sorted = [...results].sort((a, b) => {
      if (sortOrder === 'desc') {
        // Новые первые (по убыванию даты)
        return b.message.created - a.message.created;
      } else {
        // Старые первые (по возрастанию даты)
        return a.message.created - b.message.created;
      }
    });
    return sorted;
  }, [results, sortOrder]);

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>{t('search.title', 'Search Messages')}</span>
          <Tag color="blue">{roomName}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="search-messages-modal"
      destroyOnClose
    >
      <div className="search-messages-container">
        {/* Поле поиска */}
        <Search
          ref={searchInputRef}
          placeholder={t('search.placeholder', 'Search in this room...')}
          allowClear
          enterButton={
            <span>
              <SearchOutlined /> {t('search.button', 'Search')}
            </span>
          }
          size="large"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={handleSearch}
          loading={loading}
          autoFocus
          className="search-input"
        />

        {/* Результаты поиска */}
        <div className="search-results-container">
          {loading ? (
            <div className="search-loading">
              <Spin size="large" tip={t('search.searching', 'Searching...')} />
            </div>
          ) : searchPerformed ? (
            <>
              {totalResults > 0 && (
                <div className="search-results-header">
                  <div className="search-results-info">
                    <Badge 
                      count={totalResults} 
                      showZero={false}
                      overflowCount={999}
                      style={{ backgroundColor: '#52c41a' }}
                    />
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      {t('search.resultsFound', {
                        count: totalResults,
                        defaultValue: `Found ${totalResults} result(s)`
                      })}
                    </Text>
                  </div>
                  <Tooltip title={
                    sortOrder === 'desc' 
                      ? t('search.sortOldestFirst', 'Sort: oldest first')
                      : t('search.sortNewestFirst', 'Sort: newest first')
                  }>
                    <Button
                      type="text"
                      icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                      onClick={toggleSortOrder}
                      className="sort-button"
                    >
                      {sortOrder === 'desc' 
                        ? t('search.newestFirst', 'Newest first')
                        : t('search.oldestFirst', 'Oldest first')
                      }
                    </Button>
                  </Tooltip>
                </div>
              )}

              {sortedResults.length > 0 ? (
                <List
                  className="search-results-list"
                  dataSource={sortedResults}
                  pagination={sortedResults.length > 20 ? {
                    current: currentPage,
                    pageSize: pageSize,
                    total: sortedResults.length,
                    showSizeChanger: true,
                    showTotal: (total) => `${t('search.total', 'Total')}: ${total}`,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    size: 'small',
                    style: { marginTop: 16, textAlign: 'center' },
                    onChange: (page, newPageSize) => {
                      setCurrentPage(page);
                      if (newPageSize && newPageSize !== pageSize) {
                        setPageSize(newPageSize);
                        setCurrentPage(1); // Сбрасываем на первую страницу при изменении размера
                      }
                    }
                  } : false}
                  renderItem={(result) => (
                    <List.Item
                      className="search-result-item"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="search-result-content">
                        {/* Заголовок результата */}
                        <div className="search-result-header">
                          <Space>
                            <Avatar 
                              size="small" 
                              icon={<UserOutlined />}
                              style={{ backgroundColor: result.message.me ? '#1677ff' : '#87d068' }}
                            />
                            <Text strong>{result.message.sender}</Text>
                            {result.message.edited && (
                              <Tag color="orange" style={{ fontSize: 11 }}>
                                {t('message.edited', 'edited')}
                              </Tag>
                            )}
                          </Space>
                          <Space>
                            <ClockCircleOutlined />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {formatDate(result.message.created)}
                            </Text>
                          </Space>
                        </div>

                        {/* Найденное сообщение */}
                        <div className="search-result-message">
                          <MessageOutlined style={{ marginRight: 8, color: '#1677ff' }} />
                          <div className="message-body">
                            <Paragraph 
                              ellipsis={{ rows: 3, expandable: true, symbol: t('common.more', 'more') }}
                              style={{ margin: 0 }}
                            >
                              {highlightMatch(result.message.content.body, searchQuery)}
                            </Paragraph>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" style={{ alignItems: "center" }}>
                      <Text >{t('search.noResults', 'No results found')}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('search.tryDifferent', 'Try a different search query')}
                      </Text>
                    </Space>
                  }
                />
              )}
            </>
          ) : (
            <div className="search-empty-state">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical">
                    <Text type="secondary">
                      {t('search.startSearching', 'Enter a search query to find messages')}
                    </Text>
                  </Space>
                }
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SearchMessages;

